import React, { useState, useRef } from 'react';
import { Upload, Button, message, Modal, List, Tag, Space, Typography } from 'antd';
import { InboxOutlined, ExclamationCircleOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { fuelFormImageApi } from './api';
import {
    ALLOWED_EXT,
    MAX_FILES_PER_BATCH,
    MAX_SIZE_MB,
    DUPLICATE_TIPS,
    validateFile,
    truncateBatch,
} from './uploaderUtils';

const { Dragger } = Upload;

/**
 * FuelFormImageUploader — 加油单图片拖拽/点选上传组件。
 *
 * 设计哲学（参考 InvoiceDataSourceUploadDrawer）：
 * 不依赖 antd Upload 的内部状态机（customRequest / onChange / status='done'），
 * 把 antd Dragger 当作"纯文件收集器" —— 在 beforeUpload 中一次性接收完整批次的
 * fileList，校验通过后立刻调用 handleSubmit 提交后端 batch 接口，再统一返回
 * Upload.LIST_IGNORE 让 antd 不要进入它自己的 fileList 状态机。
 *
 * 这样所有上传逻辑都被一个回调控制（ONE callback for one piece of state），
 * 不会出现"customRequest 没标 done 导致 onChange.allDone 永远 false"那类状态机
 * 联动问题（这是上一版踩过的坑）。
 */
const FuelFormImageUploader = ({ onUploaded, disabled }) => {
    const [uploading, setUploading] = useState(false);
    const [resultOpen, setResultOpen] = useState(false);
    const [resultData, setResultData] = useState(null);

    // 当前批次累积器：antd 会按顺序对 fileList 中的每个文件依次调用 beforeUpload，
    // 我们把通过校验的 file 攒到这里，处理完批次最后一个文件时一次性提交。
    const pendingBatchRef = useRef([]);
    const warnedTruncateRef = useRef(false);

    const handleSubmit = async (rawFiles) => {
        if (!rawFiles || rawFiles.length === 0) return;
        const { accepted: files } = truncateBatch(rawFiles);
        setUploading(true);
        try {
            const res = await fuelFormImageApi.upload(files);
            const data = res.data || res;
            setResultData(data);
            setResultOpen(true);
            const accepted = data?.accepted_count || 0;
            const dups = data?.duplicate_count || 0;
            const rejected = data?.rejected_count || 0;
            if (accepted > 0) message.success(`成功上传 ${accepted} 张`);
            if (dups > 0) message.info(`${dups} 张重复，详情见弹窗`);
            if (rejected > 0) message.warning(`${rejected} 张被拒绝，详情见弹窗`);
            onUploaded?.();
        } catch (err) {
            const detail = err?.response?.data?.detail;
            const msg = detail?.message || (typeof detail === 'string' ? detail : err.message);
            message.error('上传失败: ' + msg);
        } finally {
            setUploading(false);
        }
    };

    const beforeUpload = (file, fileList) => {
        if (uploading) {
            return Upload.LIST_IGNORE;
        }

        if (fileList.length > MAX_FILES_PER_BATCH && !warnedTruncateRef.current) {
            message.warning(`一次最多 ${MAX_FILES_PER_BATCH} 张，已截断`);
            warnedTruncateRef.current = true;
        }

        const result = validateFile(file);
        if (result.ok) {
            pendingBatchRef.current.push(file);
        } else {
            message.error(`${file.name}: ${result.reason}`);
        }

        if (file === fileList[fileList.length - 1]) {
            const batch = pendingBatchRef.current;
            pendingBatchRef.current = [];
            warnedTruncateRef.current = false;
            if (batch.length > 0) {
                handleSubmit(batch);
            }
        }

        return Upload.LIST_IGNORE;
    };

    const draggerProps = {
        name: 'files',
        multiple: true,
        accept: ALLOWED_EXT.join(','),
        showUploadList: false,
        disabled: uploading || disabled,
        beforeUpload,
    };

    const renderItem = (item) => {
        if (item.status === 'rejected') {
            return (
                <List.Item>
                    <Space>
                        <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />
                        <Typography.Text>{item.file_name}</Typography.Text>
                        <Tag color="red">拒绝</Tag>
                        <Typography.Text type="secondary">{item.duplicate_message}</Typography.Text>
                    </Space>
                </List.Item>
            );
        }
        if (item.duplicate) {
            const tip = DUPLICATE_TIPS[item.status] || { color: 'default', text: '已存在' };
            return (
                <List.Item>
                    <Space>
                        <ExclamationCircleOutlined style={{ color: '#faad14' }} />
                        <Typography.Text>{item.file_name}</Typography.Text>
                        <Tag color={tip.color}>{tip.text}</Tag>
                    </Space>
                </List.Item>
            );
        }
        return (
            <List.Item>
                <Space>
                    <CheckCircleOutlined style={{ color: '#52c41a' }} />
                    <Typography.Text>{item.file_name}</Typography.Text>
                    <Tag color="blue">{item.status}</Tag>
                </Space>
            </List.Item>
        );
    };

    return (
        <>
            <Dragger {...draggerProps} style={{ padding: '8px 0' }}>
                <p className="ant-upload-drag-icon"><InboxOutlined /></p>
                <p className="ant-upload-text">
                    {uploading ? '上传中…' : '点击或拖拽图片到此处上传'}
                </p>
                <p className="ant-upload-hint">
                    支持 PNG / JPG / WEBP / HEIC，单张最大 {MAX_SIZE_MB} MB，一次最多 {MAX_FILES_PER_BATCH} 张
                </p>
            </Dragger>

            <Modal
                title="上传结果"
                open={resultOpen}
                onCancel={() => setResultOpen(false)}
                footer={[<Button key="ok" type="primary" onClick={() => setResultOpen(false)}>知道了</Button>]}
                width={520}
            >
                {resultData && (
                    <Space direction="vertical" size="small" style={{ width: '100%' }}>
                        <Typography.Text>
                            成功 {resultData.accepted_count || 0} · 重复 {resultData.duplicate_count || 0}
                            {' · '}拒绝 {resultData.rejected_count || 0}
                        </Typography.Text>
                        <List
                            size="small"
                            dataSource={resultData.items || []}
                            renderItem={renderItem}
                            style={{ maxHeight: 360, overflow: 'auto' }}
                        />
                    </Space>
                )}
            </Modal>
        </>
    );
};

export default FuelFormImageUploader;
